import z from "zod"

import { CapabilityIdentifier } from "./dtos"

/**
 * Semantic Skipped/Error reason keys per capability.
 *
 * These keys are what `internalExecute` returns and what is persisted in the
 * `CapabilityExecution.reason` column. The backend resolves a key to the agent-facing prompt
 * via a per-capability prompt dictionary, and the operator UI resolves it to a human label via
 * `CAPABILITY_REASON_LABELS`. The two are intentionally independent: the agent prompt and the
 * operator label can diverge without coupling.
 */

export const CancelOrderReason = {
  MissingFields: "missing_fields",
  OrderNotFound: "order_not_found",
  OrderContactMismatch: "order_contact_mismatch",
  OrderEmailMissing: "order_email_missing",
  OrderEmailMismatch: "order_email_mismatch",
  OrderIdMismatch: "order_id_mismatch",
  ConfirmationRequired: "confirmation_required",
  ReasonRequired: "reason_required",
  AlreadyCancelled: "already_cancelled",
  NotCancellable: "not_cancellable",
  WindowPassed: "window_passed",
} as const

export type CancelOrderReason = (typeof CancelOrderReason)[keyof typeof CancelOrderReason]

export const UpdateOrderReason = {
  MissingFields: "missing_fields",
  NotEligible: "not_eligible",
  ShippingAddressMissing: "shipping_address_missing",
  ShippingAddressInvalid: "shipping_address_invalid",
  ShippingCountryChangeNotSupported: "shipping_country_change_not_supported",
  PickupPointNotSupported: "pickup_point_not_supported",
  RecipientNameMissing: "recipient_name_missing",
  ConfirmationRequired: "confirmation_required",
  UpdateFailed: "update_failed",
  PriorityUpdateFailed: "priority_update_failed",
  OnHoldFailed: "on_hold_failed",
  AlreadyShipped: "already_shipped",
  OrderNotSynced: "order_not_synced",
  OrderNotFound: "order_not_found",
  OrderContactMismatch: "order_contact_mismatch",
  OrderEmailMissing: "order_email_missing",
  OrderEmailMismatch: "order_email_mismatch",
  OrderIdMismatch: "order_id_mismatch",
  OrderUpdateMultipleProducts: "order_update_multiple_products",
  LineItemDiscounted: "line_item_discounted",
  ProductVariantMissing: "product_variant_missing",
  ProductVariantAmbiguous: "product_variant_ambiguous",
  ProductVariantOutOfStock: "product_variant_out_of_stock",
  ProductVariantPriceMismatch: "product_variant_price_mismatch",
  LineItemIdMissing: "line_item_id_missing",
  LineItemUpdateFailed: "line_item_update_failed",
  ShopifyLineItemUpdateFailed: "shopify_line_item_update_failed",
  UnexpectedError: "unexpected_error",
  UnsupportedAction: "unsupported_action",
} as const

export type UpdateOrderReason = (typeof UpdateOrderReason)[keyof typeof UpdateOrderReason]

export const GetOrderTrackingReason = {
  MissingFields: "missing_fields",
  OrderNotFound: "order_not_found",
  OrderContactMismatch: "order_contact_mismatch",
  OrderEmailMissing: "order_email_missing",
  OrderEmailMismatch: "order_email_mismatch",
  OrderIdMismatch: "order_id_mismatch",
} as const

export type GetOrderTrackingReason =
  (typeof GetOrderTrackingReason)[keyof typeof GetOrderTrackingReason]

export const FindOrderNumberReason = {
  MissingFields: "missing_fields",
  VerificationFailed: "verification_failed",
  VerificationNotUnique: "verification_not_unique",
} as const

export type FindOrderNumberReason =
  (typeof FindOrderNumberReason)[keyof typeof FindOrderNumberReason]

export const GetOrderRefundStatusReason = {
  MissingFields: "missing_fields",
  RefundNotFound: "refund_not_found",
  OrderContactMismatch: "order_contact_mismatch",
  RefundEmailMismatch: "refund_email_mismatch",
  OrderIdMismatch: "order_id_mismatch",
} as const

export type GetOrderRefundStatusReason =
  (typeof GetOrderRefundStatusReason)[keyof typeof GetOrderRefundStatusReason]

export const InstructionCapabilityReason = {
  MissingFields: "missing_fields",
  InvalidData: "invalid_data",
} as const

export type InstructionCapabilityReason =
  (typeof InstructionCapabilityReason)[keyof typeof InstructionCapabilityReason]

export const SearchCatalogReason = {
  MissingSearchQuery: "missing_search_query",
} as const

export type SearchCatalogReason = (typeof SearchCatalogReason)[keyof typeof SearchCatalogReason]

export const BusinessOpportunityReason = {
  MissingRequiredFields: "missing_required_fields",
  ContactRequirementNotMet: "contact_requirement_not_met",
} as const

export type BusinessOpportunityReason =
  (typeof BusinessOpportunityReason)[keyof typeof BusinessOpportunityReason]

export const ProductInformationReason = {
  AmbiguousReference: "ambiguous_reference",
  NoGroundedReference: "no_grounded_reference",
  VariantAmbiguous: "variant_ambiguous",
  VariantNoMatch: "variant_no_match",
  MissingSearchQuery: "missing_search_query",
} as const

export type ProductInformationReason =
  (typeof ProductInformationReason)[keyof typeof ProductInformationReason]

export const CapabilityBaseReason = {
  MissingRequirements: "missing_requirements",
  ConsecutiveSkipLimitReached: "consecutive_skip_limit_reached",
  ConsecutiveUnsuccessLimitReached: "consecutive_unsuccess_limit_reached",
  TestingEnvironment: "testing_environment",
  UnexpectedError: "unexpected_error",
} as const

export type CapabilityBaseReason = (typeof CapabilityBaseReason)[keyof typeof CapabilityBaseReason]

export type TReason =
  | CapabilityBaseReason
  | CancelOrderReason
  | UpdateOrderReason
  | FindOrderNumberReason
  | GetOrderTrackingReason
  | GetOrderRefundStatusReason
  | InstructionCapabilityReason
  | SearchCatalogReason
  | BusinessOpportunityReason
  | ProductInformationReason

const capabilityReasonValues = [
  ...Object.values(CapabilityBaseReason),
  ...Object.values(CancelOrderReason),
  ...Object.values(UpdateOrderReason),
  ...Object.values(FindOrderNumberReason),
  ...Object.values(GetOrderTrackingReason),
  ...Object.values(GetOrderRefundStatusReason),
  ...Object.values(InstructionCapabilityReason),
  ...Object.values(SearchCatalogReason),
  ...Object.values(BusinessOpportunityReason),
  ...Object.values(ProductInformationReason),
] satisfies TReason[]

export const CapabilityReasonValues = [...new Set(capabilityReasonValues)] as [
  TReason,
  ...TReason[],
]

export const CapabilityReasonSchema = z.enum(CapabilityReasonValues)

export function normalizeCapabilityReason(reason: string | null | undefined): TReason | null {
  if (!reason) return null

  const parsed = CapabilityReasonSchema.safeParse(reason)
  if (parsed.success) return parsed.data

  if (reason.startsWith("Missing requirements:")) return CapabilityBaseReason.MissingRequirements

  return CapabilityBaseReason.UnexpectedError
}

const CAPABILITY_BASE_REASON_LABELS: Record<CapabilityBaseReason, string> = {
  [CapabilityBaseReason.MissingRequirements]: "Missing requirements",
  [CapabilityBaseReason.ConsecutiveSkipLimitReached]: "Consecutive skip limit reached",
  [CapabilityBaseReason.ConsecutiveUnsuccessLimitReached]: "Consecutive unsuccess limit reached",
  [CapabilityBaseReason.TestingEnvironment]: "Testing environment",
  [CapabilityBaseReason.UnexpectedError]: "Unexpected error",
}

/**
 * Operator-facing labels for the semantic reason keys above. Used by the conversation timeline and
 * the executions sheet to render a human message instead of the raw key. Capabilities not listed
 * here (or rows that predate the semantic keys) fall back to the raw stored reason string.
 */
export const CAPABILITY_REASON_LABELS: Partial<
  Record<CapabilityIdentifier, Record<string, string>>
> = {
  [CapabilityIdentifier.FindOrderNumber]: {
    [FindOrderNumberReason.MissingFields]: "Missing order verification fields",
    [FindOrderNumberReason.VerificationFailed]: "Order verification failed",
    [FindOrderNumberReason.VerificationNotUnique]: "Order verification was not unique",
  },
  [CapabilityIdentifier.CancelOrder]: {
    [CancelOrderReason.MissingFields]: "Missing order ID or contact",
    [CancelOrderReason.OrderNotFound]: "Order not found",
    [CancelOrderReason.OrderContactMismatch]: "Order contact mismatch",
    [CancelOrderReason.OrderEmailMissing]: "Order email missing",
    [CancelOrderReason.OrderEmailMismatch]: "Order email mismatch",
    [CancelOrderReason.OrderIdMismatch]: "Order ID mismatch",
    [CancelOrderReason.ConfirmationRequired]: "Waiting for cancellation confirmation",
    [CancelOrderReason.ReasonRequired]: "Waiting for cancellation reason",
    [CancelOrderReason.AlreadyCancelled]: "Order already cancelled",
    [CancelOrderReason.NotCancellable]: "Order cannot be cancelled",
    [CancelOrderReason.WindowPassed]: "Cancellation window has passed",
  },
  [CapabilityIdentifier.UpdateOrder]: {
    [UpdateOrderReason.MissingFields]: "Missing order ID or contact",
    [UpdateOrderReason.NotEligible]: "Order not eligible for update",
    [UpdateOrderReason.ShippingAddressMissing]: "New shipping address missing",
    [UpdateOrderReason.ShippingAddressInvalid]: "Invalid shipping address",
    [UpdateOrderReason.ShippingCountryChangeNotSupported]: "Shipping country change not supported",
    [UpdateOrderReason.PickupPointNotSupported]: "Delivery cannot be changed to a pickup point",
    [UpdateOrderReason.RecipientNameMissing]: "New recipient name missing",
    [UpdateOrderReason.ConfirmationRequired]: "Waiting for address change confirmation",
    [UpdateOrderReason.UpdateFailed]: "Order update failed",
    [UpdateOrderReason.PriorityUpdateFailed]: "Priority update failed",
    [UpdateOrderReason.OnHoldFailed]: "Order could not be put on hold",
    [UpdateOrderReason.AlreadyShipped]: "Order already shipped",
    [UpdateOrderReason.OrderNotSynced]: "Order not synced to logistics provider",
    [UpdateOrderReason.OrderNotFound]: "Order not found",
    [UpdateOrderReason.OrderContactMismatch]: "Order contact mismatch",
    [UpdateOrderReason.OrderEmailMissing]: "Order email missing",
    [UpdateOrderReason.OrderEmailMismatch]: "Order email mismatch",
    [UpdateOrderReason.OrderIdMismatch]: "Order ID mismatch",
    [UpdateOrderReason.OrderUpdateMultipleProducts]: "Product update requires one line item",
    [UpdateOrderReason.LineItemDiscounted]: "Product line has discounts",
    [UpdateOrderReason.ProductVariantMissing]: "Replacement product variant missing",
    [UpdateOrderReason.ProductVariantAmbiguous]: "Replacement product variant is ambiguous",
    [UpdateOrderReason.ProductVariantOutOfStock]: "Replacement product variant out of stock",
    [UpdateOrderReason.ProductVariantPriceMismatch]: "Replacement product variant price mismatch",
    [UpdateOrderReason.LineItemIdMissing]: "Line item ID missing",
    [UpdateOrderReason.LineItemUpdateFailed]: "Product update failed",
    [UpdateOrderReason.ShopifyLineItemUpdateFailed]: "Shopify product update failed",
    [UpdateOrderReason.UnexpectedError]: "Could not retrieve order information",
    [UpdateOrderReason.UnsupportedAction]: "Unsupported update action",
  },
  [CapabilityIdentifier.GetOrderTracking]: {
    [GetOrderTrackingReason.MissingFields]: "Missing order ID or contact",
    [GetOrderTrackingReason.OrderNotFound]: "Order not found",
    [GetOrderTrackingReason.OrderContactMismatch]: "Order contact mismatch",
    [GetOrderTrackingReason.OrderEmailMissing]: "Order email missing",
    [GetOrderTrackingReason.OrderEmailMismatch]: "Order email mismatch",
    [GetOrderTrackingReason.OrderIdMismatch]: "Order ID mismatch",
  },
  [CapabilityIdentifier.GetOrderRefundStatus]: {
    [GetOrderRefundStatusReason.MissingFields]: "Missing order ID or contact",
    [GetOrderRefundStatusReason.RefundNotFound]: "Refund record not found",
    [GetOrderRefundStatusReason.OrderContactMismatch]: "Order contact mismatch",
    [GetOrderRefundStatusReason.RefundEmailMismatch]: "Refund email mismatch",
    [GetOrderRefundStatusReason.OrderIdMismatch]: "Order ID mismatch",
  },
  [CapabilityIdentifier.Instruction]: {
    [InstructionCapabilityReason.MissingFields]: "Missing required fields",
    [InstructionCapabilityReason.InvalidData]: "Instruction execution produced invalid data",
  },
  [CapabilityIdentifier.SearchCatalog]: {
    [SearchCatalogReason.MissingSearchQuery]: "Missing search query",
  },
  [CapabilityIdentifier.BusinessOpportunity]: {
    [BusinessOpportunityReason.MissingRequiredFields]: "Missing required fields",
    [BusinessOpportunityReason.ContactRequirementNotMet]: "Contact requirement not met",
  },
  [CapabilityIdentifier.ProductInformation]: {
    [ProductInformationReason.AmbiguousReference]: "Product reference is ambiguous",
    [ProductInformationReason.NoGroundedReference]: "No product reference resolved",
    [ProductInformationReason.VariantAmbiguous]: "Multiple matching variants",
    [ProductInformationReason.VariantNoMatch]: "No matching variant",
    [ProductInformationReason.MissingSearchQuery]: "Missing search query",
  },
}

/**
 * Resolve a stored reason to its operator-facing label. Falls back to the raw reason string for
 * capabilities without semantic keys or rows written before this contract existed.
 */
export function resolveCapabilityReasonLabel(
  identifier: CapabilityIdentifier | string,
  reason: string | null | undefined,
) {
  if (!reason) return null
  const labels = CAPABILITY_REASON_LABELS[identifier as CapabilityIdentifier]
  return labels?.[reason] ?? CAPABILITY_BASE_REASON_LABELS[reason as CapabilityBaseReason] ?? reason
}
